/** 

=========================================================
* Vision UI PRO React - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Visionware.

*/

import { useState, useEffect } from 'react';

// @mui material components
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';

// Vision UI Dashboard PRO React components
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';
import VuiInput from 'components/VuiInput';
import VuiSelect from 'components/VuiSelect';
import VuiPagination from 'components/VuiPagination';
import Icon from '@mui/material/Icon';

// Vision UI Dashboard PRO React example components
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';

// API Services
import { takedownsApi } from 'services/takedownsApi';

// Data transformation utilities
import { 
  transformPlatformsData, 
  getPlatformsFilterButtons, 
  createEmptyPlatformsData 
} from 'utils/platformsDataTransform';


function Platforms() {
  // State management
  const [activeFilter, setActiveFilter] = useState(null);
  const [platformsData, setPlatformsData] = useState(createEmptyPlatformsData());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiParams, setApiParams] = useState({
    page: 1,
    limit: 20,
    search: undefined,
    sort_by: 'totalTakedowns',
    sort_order: 'DESC'
  });
  const [availableFilters, setAvailableFilters] = useState([]);
  const [counts, setCounts] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch platforms data from API
  const fetchPlatformsData = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const requestParams = { ...apiParams, ...params };
      setApiParams(requestParams);


      // Fetch data and counts in parallel
      const [dataResponse, countsResponse] = await Promise.all([
        takedownsApi.fetchAllPlatforms(requestParams),
        takedownsApi.fetchPlatformsCounts(requestParams).catch(() => null) // Don't fail if counts endpoint is unavailable
      ]);


      // Transform the data for the DataTable
      const transformedData = transformPlatformsData(dataResponse.data, dataResponse.pagination);
      setPlatformsData(transformedData);

      // Update available filters and counts
      if (countsResponse) {
        setCounts(countsResponse);
        const filterButtons = getPlatformsFilterButtons(countsResponse, handleFilterChange, activeFilter);
        setAvailableFilters(filterButtons);
      } else {
        // Fallback filter buttons if counts are not available
        setAvailableFilters([
          { key: 'poorCompliance', label: 'Poor Compliance', filter: { compliance_rate: 'poor' } },
          { key: 'Explicit', label: 'Explicit', filter: { violation_type: 'Explicit' } },
          { key: 'Copyright', label: 'Copyright', filter: { violation_type: 'Copyright' } }
        ]);
      }

    } catch (err) {
      setError(err.message || 'Failed to fetch platforms data');
      setPlatformsData(createEmptyPlatformsData());
    } finally {
      setLoading(false);
    }
  };

  // Fetch counts only (for filter updates)
  const fetchCountsOnly = async (filters = {}) => {
    try {
      const requestParams = { ...apiParams, ...filters };
      const countsResponse = await takedownsApi.fetchPlatformsCounts(requestParams);
      setCounts(countsResponse);
      
      const filterButtons = getPlatformsFilterButtons(countsResponse, handleFilterChange, activeFilter);
      setAvailableFilters(filterButtons);
    } catch (err) {
      // Don't update state on error, keep existing filters
    }
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, filterValue) => {
    setActiveFilter(filterKey);
    fetchPlatformsData({ 
      page: 1, // Reset to first page when filtering
      ...filterValue,
      search: undefined // Clear search when filtering
    });
    setSearchTerm(''); // Clear search term
  };

  // Handle page changes
  const handlePageChange = (page) => {
    fetchPlatformsData({ page });
  };

  // Handle search with debouncing
  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      fetchPlatformsData({ 
        page: 1, // Reset to first page when searching
        search: value || undefined
      });
      setActiveFilter(null); // Clear active filter when searching
    }, 500);

    setSearchTimeout(timeout);
  };

  // Handle entries per page changes
  const handleEntriesPerPageChange = (entriesPerPage) => {
    fetchPlatformsData({ 
      page: 1, // Reset to first page when changing entries per page
      limit: entriesPerPage 
    });
  };

  // Load data on component mount
  useEffect(() => {
    fetchPlatformsData();
    
    // Cleanup timeout on unmount
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, []);

  // Get filter buttons with counts
  const getFilterButtons = () => {
    return availableFilters.map((filter) => (
      <VuiButton
        key={filter.key}
        color={activeFilter === filter.key ? 'info' : 'dark'}
        variant={activeFilter === filter.key ? 'contained' : 'outlined'}
        onClick={() => handleFilterChange(filter.key, filter.filter)}
        sx={{
          border: activeFilter !== filter.key ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
          '&:hover': {
            backgroundColor:
              activeFilter === filter.key ? '#0075FF' : 'rgba(255, 255, 255, 0.05)',
          },
        }}
      >
        {filter.label}
      </VuiButton>
    ));
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3} sx={{ overflow: "visible" }}>
        {/* Header */}
        <VuiBox mb={3}>
          <VuiTypography variant="h3" color="white" fontWeight="bold">
            Platform Tracker Dashboard
          </VuiTypography>
        </VuiBox>

        {/* Search and Filter Controls */}
        <VuiBox mb={3} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          {/* Search Input */}
          <VuiBox width="300px">
            <VuiInput
              placeholder="Search by platform, type, or status"
              value={searchTerm}
              onChange={handleSearch}
              icon={{
                component: "search",
                direction: "left",
              }}
            />
          </VuiBox>

          {/* Filter Buttons */}
          <VuiBox display="flex" gap={2}>
            {getFilterButtons()}
          </VuiBox>

          {/* Clear Filter Button - Only show when there are active filters */}
          {(activeFilter || searchTerm) && (
            <VuiButton
              color="dark"
              variant="outlined"
              onClick={() => {
                setActiveFilter(null);
                // Clear all filters and reset to page 1
                fetchPlatformsData({ 
                  page: 1,
                  compliance_rate: undefined,
                  violation_type: undefined,
                  search: undefined
                });
                // Clear search term
                setSearchTerm('');
              }}
              sx={{
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                },
              }}
            >
              Clear Filter
            </VuiButton>
          )}
        </VuiBox>

        {/* Loading State */}
        {loading && (
          <VuiBox display="flex" justifyContent="center" py={4}>
            <CircularProgress color="info" />
          </VuiBox>
        )}

        {/* Error State */}
        {error && (
          <VuiBox mb={3}>
            <Alert severity="error" sx={{ backgroundColor: 'rgba(255, 107, 107, 0.1)', color: 'white' }}>
              {error}
            </Alert>
          </VuiBox>
        )}

        {/* Main Content - Only show when not loading and no error */}
        {!loading && !error && (
          <Card sx={{ overflow: "visible" }}>
            {/* Data Table */}
            <VuiBox>
              <DataTable
                table={platformsData}
                entriesPerPage={{
                  defaultValue: apiParams.limit,
                  entries: [10, 20, 50, 100],
                }}
                canSearch={false} // Disable built-in search
                showTotalEntries={true}
                isSorted={true}
                noEndBorder={false}
                pagination={{
                  variant: 'contained',
                  color: 'info',
                  currentPage: platformsData.pagination?.current_page || 1,
                  totalPages: platformsData.pagination?.total_pages || 1,
                  totalItems: platformsData.pagination?.total_items || 0,
                }}
                onPageChange={handlePageChange}
                onEntriesPerPageChange={handleEntriesPerPageChange}
              />
            </VuiBox>
          </Card>
        )}
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Platforms;
